import qz from 'qz-tray';

import { RestaurantKitchenPrintJobDTO } from '../../types/restaurant/RestaurantKitchenOrderTypes';
import QzTraySecurityService from '../qztray/QzTraySecurityService';
import { desktopBridge } from './desktop-bridge';

export interface KitchenPrintJobResult {
  job: RestaurantKitchenPrintJobDTO;
  success: boolean;
  error?: unknown;
}

export const printKitchenJob = async (
  job: RestaurantKitchenPrintJobDTO
): Promise<void> => {
  if (!job.contentBase64?.trim()) {
    throw new Error('El trabajo no contiene contentBase64.');
  }

  const bridge = desktopBridge.get();

  if (job.desktopPrintingEnabled === true && bridge?.printRaw) {
    await bridge.printRaw({
      jobId: job.jobId,
      printerRole: job.printerRole,
      printerName: job.printerName,
      format: job.format,
      contentBase64: job.contentBase64,
      copies: Math.max(1, Number(job.copies || 1)),
    });
    return;
  }

  QzTraySecurityService.configure();
  if (!qz.websocket.isActive()) await qz.websocket.connect();

  const printer =
    job.connectionType === 'NETWORK'
      ? { host: job.ipAddress, port: job.port }
      : job.printerName;

  if (job.connectionType === 'NETWORK' && (!job.ipAddress || !job.port)) {
    throw new Error('La impresora de red no tiene dirección IP y puerto.');
  }

  const config = qz.configs.create(printer, {
    copies: Math.max(1, Number(job.copies || 1)),
    jobName: job.jobId,
    rasterize: false,
  });

  await qz.print(config, [
    {
      type: 'raw',
      format: 'base64',
      data: job.contentBase64,
    },
  ]);
};

export const processKitchenPrintJobs = async (
  jobs: RestaurantKitchenPrintJobDTO[],
  print: (job: RestaurantKitchenPrintJobDTO) => Promise<void> = printKitchenJob,
  onStatus?: (
    job: RestaurantKitchenPrintJobDTO,
    status: 'PRINTING' | 'PRINTED' | 'ERROR',
    error?: unknown
  ) => void
): Promise<KitchenPrintJobResult[]> => {
  const results: KitchenPrintJobResult[] = [];

  for (const job of jobs) {
    onStatus?.(job, 'PRINTING');
    try {
      await print(job);
      results.push({ job, success: true });
      onStatus?.(job, 'PRINTED');
    } catch (error) {
      results.push({ job, success: false, error });
      onStatus?.(job, 'ERROR', error);
    }
  }

  return results;
};
